import React, { useEffect, useState } from "react";
import {
  validateName, validateEmail, validateAddressLines,
  validatePostCode, validateContactNumber,
} from "../../Validations";
import { ClientBasicDetails } from "./AddClientsComponents/ClientBasicDetails";
import { useClientContext } from "../../../_context/ClientContext";
import { ClientAddressDetails } from "./AddClientsComponents/ClientAddressDetails";
import { ClientAdminDetails } from "./AddClientsComponents/ClientAdminDetails";
import { useUserContext } from "../../../_context/UserContext";
import { ClientCleanerDetails } from "./AddClientsComponents/ClientCleanerDetails";
import EmptyClient from "../../../_resources/JsonData/EmptyClient.json";
import { addClient, getAllClients } from "../../../_utils/resourceAPI";
import { Client } from "../../../_models/Client.model";
import { AddClientSuccessModal } from "./AddClientsComponents/AddClientSuccessModal";
import { createDateISO, expiredDateISO } from "../../../_constrains/dates";
import { ClientTypeDetails } from "./AddClientsComponents/ClientTypeDetails";
import { ClientReportingDetails } from "./AddClientsComponents/ClientReportingUserDetails";
import "./AddClients.css";
import "../AddUsers/AddUsers.css";

/* ── SVG chevron ── */
const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/* ── Accordion section definition ── */
interface AccordionSection {
  key: string;
  title: string;
  subtitle: string;
  iconTheme: "blue" | "gold" | "purple" | "green" | "teal";
  icon: React.ReactNode;
  content: React.ReactNode;
}

/* ── Reusable accordion section ── */
const AcSection: React.FC<AccordionSection & { defaultOpen?: boolean }> = ({
  title, subtitle, iconTheme, icon, content, defaultOpen = false
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`ac-section${open ? " open" : ""}`}>
      <button className="ac-section-header" onClick={() => setOpen(o => !o)}>
        <div className="ac-section-header-left">
          <div className={`ac-section-icon ${iconTheme}`}>{icon}</div>
          <div>
            <p className="ac-section-title">{title}</p>
            <p className="ac-section-subtitle">{subtitle}</p>
          </div>
        </div>
        <span className="ac-section-chevron"><ChevronIcon /></span>
      </button>
      {open && <div className="ac-section-body">{content}</div>}
    </div>
  );
};

/* ── Main component ── */
export const AddClients = () => {
  const { client, setClient, setAllClients, setClientAdded } = useClientContext();
  const { setCurrentPage } = useUserContext();

  const isFormValid = () =>
    !validateName(client.Name, true) &&
    !validateEmail(client.Email, true) &&
    !validateAddressLines(client.Address.Line1, true) &&
    !validateAddressLines(client.Address.Line2, false) &&
    !validatePostCode(client.Address.Postcode.toString(), true);

  const fetchClients = () => {
    getAllClients()
      .then(res => setAllClients(res.data as Client[]))
      .catch(() => console.log("Failed to fetch clients"));
  };

  useEffect(() => {
    setCurrentPage("Add Client");
    if (client.Id) setClient(EmptyClient);
  }, []);

  const handleFormSubmit = () => {
    const newClientData = { ...client, CreateDate: createDateISO, ExpiredDate: expiredDateISO };
    addClient(newClientData)
      .then(() => { fetchClients(); setClient(EmptyClient); setClientAdded(true); })
      .catch(err => console.log(err));
  };

  return (
    <div className="add-clients-page">
      <div className="ac-accordion">

        <AcSection
          key="contact"
          title="Client Contact Information"
          subtitle="Name, email, and address details"
          iconTheme="blue"
          defaultOpen={true}
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          }
          content={
            <>
              <ClientBasicDetails clientNameValidator={validateName} clientemailValidator={validateEmail} />
              <ClientAddressDetails addressValidator={validateAddressLines} postCodeValidator={validatePostCode} />
            </>
          }
        />

        <AcSection
          key="type"
          title="Client Type"
          subtitle="Select the service category"
          iconTheme="gold"
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          }
          content={<ClientTypeDetails />}
        />

        <AcSection
          key="admin"
          title="Client Admin Information"
          subtitle="Admin contact and authority details"
          iconTheme="purple"
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          }
          content={
            <ClientAdminDetails
              adminEmailValidator={validateEmail}
              adminContactValidator={validateContactNumber}
              adminNameValidator={validateName}
            />
          }
        />

        <AcSection
          key="cleaner"
          title="Assigned Cleaner"
          subtitle="Link a cleaner to this client"
          iconTheme="green"
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 20l-7-7" /><path d="M9 3l-.5 9.5L4 18h16l-4.5-5.5L15 3H9z" />
            </svg>
          }
          content={<ClientCleanerDetails />}
        />

        <AcSection
          key="supervisor"
          title="Supervisor Details"
          subtitle="Reporting and supervision information"
          iconTheme="teal"
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          }
          content={<ClientReportingDetails />}
        />

      </div>

      <button className="ac-submit-btn" onClick={handleFormSubmit} disabled={!isFormValid()}>
        Add Client
      </button>

      <AddClientSuccessModal />
    </div>
  );
};
